import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';

import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { FlexCard } from 'components/token/FlexCard';

import type {
  PriceHistoryQuery,
  PriceHistoryQueryVariables,
  useGetPriceHistory_anyCard,
} from './__generated__/useGetPriceHistory.graphql';

const cardFragment = gql`
  fragment useGetPriceHistory_anyCard on AnyCardInterface {
    slug
    sport
    pictureUrl
    rarityTyped
    anyPlayer {
      slug
      displayName
    }
    user {
      slug
    }
    ...FlexCard_anyCard
  }
  ${FlexCard.fragments.anyCard}
` as TypedDocumentNode<useGetPriceHistory_anyCard>;

const PRICE_HISTORY_QUERY = gql`
  query PriceHistoryQuery(
    $playerSlug: String!
    $rarity: Rarity!
    $seasonEligibility: SeasonEligibility
    $from: ISO8601DateTime
  ) {
    tokens {
      tokenPrices(
        playerSlug: $playerSlug
        rarity: $rarity
        seasonEligibility: $seasonEligibility
        from: $from
      ) {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        date
        deal {
          ... on TokenAuction {
            id
            cards: anyCards {
              slug
              ...useGetPriceHistory_anyCard
            }
          }
          ... on TokenOffer {
            id
            sender {
              ... on User {
                slug
                nickname
                suspended
                pictureUrl(derivative: "avatar")
                ...Avatar_user
              }
              ... on AnonymousUser {
                id
              }
            }
            senderSide {
              id
              cards: anyCards {
                slug
                ...useGetPriceHistory_anyCard
              }
            }
          }
          ... on TokenPrimaryOffer {
            id
            cards: anyCards {
              slug
              ...useGetPriceHistory_anyCard
            }
          }
        }
      }
    }
  }
  ${monetaryAmountFragment}
  ${cardFragment}
  ${Avatar.fragments.user}
` as TypedDocumentNode<PriceHistoryQuery, PriceHistoryQueryVariables>;

export const useGetPriceHistory = (args: PriceHistoryQueryVariables) => {
  const prepareAcceptOffer = useQuery(PRICE_HISTORY_QUERY, {
    variables: args,
  });
  return prepareAcceptOffer;
};

export const useLazyGetPriceHistory = () => {
  return useLazyQuery(PRICE_HISTORY_QUERY);
};
